import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import API, { runGlobalApiErrorHandler } from '../../../../services/API'
import { EventControlDashboardContext } from './Dashboard'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import EventIcon from '@mui/icons-material/Event'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import SensorsIcon from '@mui/icons-material/Sensors'
import StartControlRoomPopup from './StartControlRoomPopup'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'
import { UserContext } from '../../../../App'
import ConcurrentRoomLimitAlert from '../../../../components/Subscriptions/ConcurrentRoomLimitAlert'
import { isControlRoomLimit } from '../../../../helpers/controlRoomCapacity'

const UpcomingEvents = () => {
  const { setSiteMode, setSelectedControlRoom } = useContext(SiteModeContext)
  const { user } = useContext(UserContext)
  // Starting/reopening a control room is a commander decision — everyone
  // else can still enter a room that already exists.
  const isCommander =
    !!user?.security?.isSiteAdmin || user?.employee?.cadAccess === 'Commander'
  const [upcomingEvents, setUpcomingEvents] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const [loading, setLoading] = useState(true)
  const { tab } = useContext(EventControlDashboardContext)
  const [openStartControlRoomDialog, setOpenStartControlRoomDialog] =
    useState(false)
  // The closed control room a commander is about to enter. Reopening restarts
  // the room's clock (it uses control-room hours — a monthly limit on
  // Premium) and takes one of the plan's concurrent control-room places, so
  // it is always asked, never done silently on entry.
  const [reopenTarget, setReopenTarget] = useState(null)
  const [reopening, setReopening] = useState(false)
  const [reopenError, setReopenError] = useState('')
  // The 403 payload when the plan's concurrent control-room capacity is in
  // use — shown inline in place of the generic error.
  const [reopenLimit, setReopenLimit] = useState(null)

  const enterControlRoom = (controlRoomId) => {
    setSelectedControlRoom(controlRoomId)
    setSiteMode('eventControl')
  }

  const closeReopenDialog = () => {
    if (reopening) return
    setReopenTarget(null)
    setReopenError('')
    setReopenLimit(null)
  }

  const reopenAndEnter = async () => {
    const controlRoomId = reopenTarget?.controlRoom?._id
    if (!controlRoomId) return
    setReopening(true)
    setReopenError('')
    setReopenLimit(null)
    try {
      // skipGlobalErrorHandler so the concurrent-room refusal is explained in
      // this dialog; any other failure still goes to the global handler
      // first, as before.
      const res = await API.post(
        `/controlroom/${controlRoomId}/reopen`,
        undefined,
        { skipGlobalErrorHandler: true },
      )
      if (isControlRoomLimit(res)) {
        setReopenLimit(res.data)
        return
      }
      if (res.status !== 200) {
        if (runGlobalApiErrorHandler({ response: res })) return
        setReopenError(
          res.data?.error || 'Unable to reopen this control room. Please try again.',
        )
        return
      }
      setReopenTarget(null)
      enterControlRoom(controlRoomId)
    } catch (e) {
      if (runGlobalApiErrorHandler(e)) return
      setReopenError('Unable to reopen this control room. Please try again.')
    } finally {
      setReopening(false)
    }
  }

  const viewWithoutReopening = () => {
    const controlRoomId = reopenTarget?.controlRoom?._id
    setReopenTarget(null)
    setReopenError('')
    setReopenLimit(null)
    if (controlRoomId) enterControlRoom(controlRoomId)
  }

  useEffect(() => {
    API.get('/commandcontrol/dashboard/upcoming-events')
      .then((res) => {
        if (res.status === 200) setUpcomingEvents(res.data)
        else setFetchError(true)
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [tab])

  return (
    <Stack gap={2}>
      <Stack>
        <Typography variant="h6" fontWeight={900} color="#242F40">
          Upcoming Events
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Prepare and launch control rooms for scheduled event deployments.
        </Typography>
      </Stack>

      {loading && <LinearProgress />}
      {fetchError && (
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          Unable to load upcoming events.
        </Alert>
      )}
      {!loading && !fetchError && upcomingEvents.length === 0 && (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          There are no upcoming events to display.
        </Alert>
      )}

      <Grid container spacing={2}>
        {upcomingEvents.map((event) => {
          const hasControlRoom = !!event?.controlRoom
          const live = event?.controlRoom?.status === 'Active'

          return (
            <Grid item xs={12} md={6} lg={4} key={event._id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 10px 28px rgba(36,47,64,0.08)',
                  transition: 'all 0.22s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 18px 42px rgba(35,154,164,0.16)',
                    borderColor: '#239AA4',
                  },
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    cursor: 'pointer',
                  },
                }}
              >
                <CardContent sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1.5}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 3,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background:
                            'linear-gradient(135deg, #1F3D7C 0%, #239AA4 130%)',
                          color: 'white',
                          flexShrink: 0,
                          boxShadow: '0 12px 26px rgba(31,61,124,0.22)',
                        }}
                      >
                        <EventIcon />
                      </Box>

                      <Stack spacing={0.75} flex={1}>
                        <Typography fontWeight={900} color="#242F40">
                          {event.title}
                        </Typography>

                        <Chip
                          label={
                            !hasControlRoom
                              ? 'No Control Room'
                              : live
                                ? 'Control Live'
                                : 'Control Off'
                          }
                          size="small"
                          sx={{
                            width: 'fit-content',
                            borderRadius: 999,
                            backgroundColor: !hasControlRoom
                              ? '#E6F7F9'
                              : live
                                ? '#DCFCE7'
                                : '#FEE2E2',
                            color: !hasControlRoom
                              ? '#1E7F88'
                              : live
                                ? '#166534'
                                : '#991B1B',
                            fontWeight: 800,
                          }}
                        />
                      </Stack>
                    </Stack>

                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationOnIcon
                          sx={{ fontSize: 18, color: '#64748B' }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {event.eventLocation || 'Location not recorded'}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <SensorsIcon sx={{ fontSize: 18, color: '#64748B' }} />
                        <Typography variant="body2" color="text.secondary">
                          {moment(event.startDate).format('Do MMM HH:mm')} -{' '}
                          {moment(event.endDate).format('Do MMM HH:mm')}
                        </Typography>
                      </Stack>
                    </Stack>

                    {(isCommander || event?.controlRoom) && (
                      <Button
                        fullWidth
                        variant="contained"
                        endIcon={
                          event?.controlRoom ? (
                            <MeetingRoomIcon />
                          ) : (
                            <RocketLaunchIcon />
                          )
                        }
                        onClick={() => {
                          if (event?.controlRoom) {
                            // A commander entering a closed room is asked
                            // whether to reopen it. Everyone else — and any
                            // live room — is entered as-is.
                            if (
                              isCommander &&
                              event.controlRoom.status !== 'Active'
                            ) {
                              setReopenTarget(event)
                              return
                            }
                            enterControlRoom(event.controlRoom._id)
                          } else {
                            setOpenStartControlRoomDialog(event)
                          }
                        }}
                        sx={{
                          borderRadius: 999,
                          backgroundColor: '#239AA4',
                          textTransform: 'none',
                          fontWeight: 800,
                          '&:hover': { backgroundColor: '#1E7F88' },
                        }}
                      >
                        {event?.controlRoom
                          ? 'Enter Control Room'
                          : 'Start Control Room'}
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )
        })}
      </Grid>

      <StartControlRoomPopup
        onClose={() => setOpenStartControlRoomDialog(false)}
        event={openStartControlRoomDialog}
      />

      <Dialog
        open={!!reopenTarget}
        onClose={closeReopenDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900, color: '#242F40' }}>
          Reopen this control room?
        </DialogTitle>
        <DialogContent>
          <Stack gap={1.5}>
            <Typography variant="body2" color="text.secondary">
              The control room for <b>{reopenTarget?.title}</b> is closed and
              read-only. Reopening it makes it live again: crews can be
              assigned, it takes one of your plan's concurrently open
              control-room places, and its time counts towards your
              control-room hours until it is closed.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You can also look at the room without reopening it.
            </Typography>
            {reopenLimit && <ConcurrentRoomLimitAlert limitInfo={reopenLimit} />}
            {reopenError && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {reopenError}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={closeReopenDialog}
            disabled={reopening}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={viewWithoutReopening}
            disabled={reopening}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
          >
            View without reopening
          </Button>
          <Button
            variant="contained"
            onClick={reopenAndEnter}
            disabled={reopening}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 999,
              backgroundColor: '#239AA4',
              '&:hover': { backgroundColor: '#1E7F88' },
            }}
          >
            {reopening ? 'Reopening…' : 'Reopen control room'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default UpcomingEvents
